import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  CircularProgress,
  Alert,
  Button,
  Pagination,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BackButton from '../../components/BackButton';
import CloseIcon from '@mui/icons-material/Close';
import {
  fetchEventPaymentsWithGuests,
  approveEventPayment,
  cancelEventPayment,
  markEventPaymentAsPaid,
  payForGuestOrHost,
  payForSelectedGuests,
} from '../../controllers/paymentsController';
import { getCurrencyDisplayLabel } from '../../utils/currencyDisplay';

// Backend enum mappingleri
const EventPaymentStatus = {
  Pending: 0,
  Paid: 1,
  Cancelled: 2,
  AwaitingApproval: 3,
  PartialPaid: 4,
};

const EventPaymentStatusNames = {
  0: 'Beklemede',
  1: 'Ödendi',
  2: 'İptal Edildi',
  3: 'Onay Bekliyor',
  4: 'Kısmen Ödendi',
};

const PaymentMethod = {
  Cash: 0,
  BankTransfer: 1,
  CreditCard: 2,
  Online: 3,
};

const PaymentMethodNames = {
  0: 'Nakit',
  1: 'Banka Havalesi/EFT',
  2: 'Kredi Kartı',
  3: 'Online Ödeme',
};

const EventPayerTypeNames = {
  0: 'Üye',
  1: 'Partner',
};

const EventPaymentDetail = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();

  // Payment listesi ve pagination
  const [allPayments, setAllPayments] = useState([]); // Tüm payment'lar (istatistikler için)
  const [payments, setPayments] = useState([]); // Filtrelenmiş ve paginate edilmiş payment'lar
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50); // 600 üye için daha büyük sayfa boyutu
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Filtreler
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Yardımcı state'ler
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [eventInfo, setEventInfo] = useState(null);

  // Detay / aksiyon dialog state'leri
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [payGuestHostDialogOpen, setPayGuestHostDialogOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [selectedGuestId, setSelectedGuestId] = useState(null); // null ise host için
  const [cancelReason, setCancelReason] = useState('');
  const [paymentMethod, setPaymentMethod] = useState(PaymentMethod.Cash);
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentDescription, setPaymentDescription] = useState('');

  // Payment'ları yükle
  const loadPayments = async () => {
    if (!eventId) return;

    setLoading(true);
    setError('');
    try {
      const data = await fetchEventPaymentsWithGuests(eventId);
      
      // İlk payment'ten event bilgisini al
      if (data && data.length > 0 && !eventInfo) {
        setEventInfo({
          eventTitle: data[0].eventTitle,
          eventId: data[0].eventId,
        });
      }

      // Tüm payment'ları sakla (istatistikler için)
      setAllPayments(data || []);

      // Filtreleme
      let filteredData = data || [];
      
      if (statusFilter !== '') {
        filteredData = filteredData.filter((p) => p.status === Number(statusFilter));
      }

      if (searchTerm && searchTerm.trim() !== '') {
        const searchLower = searchTerm.toLowerCase();
        filteredData = filteredData.filter(
          (p) =>
            (p.payerName && p.payerName.toLowerCase().includes(searchLower)) ||
            (p.referenceNumber && p.referenceNumber.toLowerCase().includes(searchLower)) ||
            (p.description && p.description.toLowerCase().includes(searchLower))
        );
      }

      // Pagination
      setTotalCount(filteredData.length);
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedData = filteredData.slice(startIndex, endIndex);
      setTotalPages(Math.ceil(filteredData.length / pageSize));

      setPayments(paginatedData);
    } catch (e) {
      setError(e?.message || 'Etkinlik ödemeleri alınamadı');
      setPayments([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPayments();
  }, [eventId, page, statusFilter, searchTerm]);

  // Search submit
  const handleSearchSubmit = () => {
    setPage(1);
    loadPayments();
  };

  const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case EventPaymentStatus.Paid:
        return 'success';
      case EventPaymentStatus.AwaitingApproval:
        return 'warning';
      case EventPaymentStatus.PartialPaid:
        return 'warning';
      case EventPaymentStatus.Cancelled:
        return 'default';
      default:
        return 'info';
    }
  };

  const handleRowClick = (payment) => {
    setSelectedPayment(payment);
    setDetailDialogOpen(true);
  };

  const handleApprove = async () => {
    if (!selectedPayment) return;
    try {
      await approveEventPayment({
        EventPaymentId: selectedPayment.id,
        PaymentDate: new Date().toISOString(),
      });
      toast.success('Ödeme onaylandı');
      setApproveDialogOpen(false);
      setDetailDialogOpen(false);
      setSelectedPayment(null);
      loadPayments();
    } catch (e) {
      toast.error(e?.message || 'Ödeme onaylanamadı');
    }
  };

  const handleCancel = async () => {
    if (!selectedPayment) return;
    try {
      await cancelEventPayment({
        EventPaymentId: selectedPayment.id,
        CancellationReason: cancelReason || null,
      });
      toast.success('Ödeme iptal edildi');
      setCancelDialogOpen(false);
      setDetailDialogOpen(false);
      setSelectedPayment(null);
      setCancelReason('');
      loadPayments();
    } catch (e) {
      toast.error(e?.message || 'Ödeme iptal edilemedi');
    }
  };

  const handleMarkPaid = async () => {
    if (!selectedPayment) return;
    try {
      await markEventPaymentAsPaid({
        EventPaymentId: selectedPayment.id,
        PaymentMethod: paymentMethod,
        Description: 'Admin panel üzerinden ödendi olarak işaretlendi',
      });
      toast.success('Ödeme ödendi olarak işaretlendi');
      setMarkPaidDialogOpen(false);
      setDetailDialogOpen(false);
      setSelectedPayment(null);
      setPaymentMethod(PaymentMethod.Cash);
      loadPayments();
    } catch (e) {
      toast.error(e?.message || 'Ödeme işaretlenemedi');
    }
  };

  const handlePayForGuestOrHost = async () => {
    if (!selectedPayment) return;
    try {
      await payForGuestOrHost({
        EventPaymentId: selectedPayment.id,
        GuestId: selectedGuestId, // null ise host için
        PaymentMethod: paymentMethod,
        ReferenceNumber: referenceNumber || null,
        Description: paymentDescription || null,
      });
      toast.success(selectedGuestId ? 'Misafir ödemesi yapıldı' : 'Host ödemesi yapıldı');
      setPayGuestHostDialogOpen(false);
      setDetailDialogOpen(false);
      setSelectedPayment(null);
      setSelectedGuestId(null);
      setPaymentMethod(PaymentMethod.Cash);
      setReferenceNumber('');
      setPaymentDescription('');
      loadPayments();
    } catch (e) {
      toast.error(e?.message || 'Ödeme yapılamadı');
    }
  };

  const handleOpenPayDialog = (payment, guestId = null) => {
    setSelectedPayment(payment);
    setSelectedGuestId(guestId);
    setPaymentMethod(PaymentMethod.Cash);
    setReferenceNumber('');
    setPaymentDescription('');
    setPayGuestHostDialogOpen(true);
  };

  const canApprove = useMemo(
    () => selectedPayment && selectedPayment.status === EventPaymentStatus.AwaitingApproval,
    [selectedPayment],
  );

  const canCancel = useMemo(
    () =>
      selectedPayment &&
      selectedPayment.status !== EventPaymentStatus.Paid &&
      selectedPayment.status !== EventPaymentStatus.Cancelled,
    [selectedPayment],
  );

  const canMarkPaid = useMemo(
    () => selectedPayment && selectedPayment.status === EventPaymentStatus.Pending,
    [selectedPayment],
  );

  // İstatistikler (tüm payment'lardan hesaplanıyor)
  const stats = useMemo(() => {
    if (!allPayments || allPayments.length === 0) {
      return {
        total: 0,
        paid: 0,
        pending: 0,
        awaitingApproval: 0,
        cancelled: 0,
        totalAmount: 0,
        paidAmount: 0,
      };
    }

    return {
      total: allPayments.length,
      paid: allPayments.filter((p) => p.status === EventPaymentStatus.Paid).length,
      pending: allPayments.filter((p) => p.status === EventPaymentStatus.Pending).length,
      awaitingApproval: allPayments.filter((p) => p.status === EventPaymentStatus.AwaitingApproval).length,
      cancelled: allPayments.filter((p) => p.status === EventPaymentStatus.Cancelled).length,
      totalAmount: allPayments.reduce((sum, p) => sum + (p.amount || 0), 0),
      paidAmount: allPayments
        .filter((p) => p.status === EventPaymentStatus.Paid)
        .reduce((sum, p) => sum + (p.amount || 0), 0),
    };
  }, [allPayments]);

  if (loading && payments.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <BackButton onClick={() => navigate('/payments/event-dues')} />
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, mt: 2 }}>
        <Box>
          <Typography sx={{ fontSize: '18px', fontWeight: 500, color: '#444444' }}>
            {eventInfo?.eventTitle || 'Etkinlik Ödemeleri'}
          </Typography>
          <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mt: 0.5 }}>
            Toplam {totalCount} ödeme kaydı
          </Typography>
        </Box>
      </Stack>

      {/* İstatistikler */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <PeopleIcon sx={{ fontSize: 24, color: '#0E4D94' }} />
                <Box>
                  <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#444444' }}>{stats.total}</Typography>
                  <Typography sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                    Toplam Ödeme
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <CheckCircleIcon sx={{ fontSize: 24, color: '#17C653' }} />
                <Box>
                  <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#444444' }}>{stats.paid}</Typography>
                  <Typography sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                    Ödendi
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <AttachMoneyIcon sx={{ fontSize: 24, color: '#FF9800' }} />
                <Box>
                  <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#444444' }}>
                    {stats.totalAmount.toLocaleString('tr-TR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    TL
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                    Toplam Tutar
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ border: '1px solid #e7e7e7', boxShadow: 'none', borderRadius: '8px' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <CheckCircleIcon sx={{ fontSize: 24, color: '#17C653' }} />
                <Box>
                  <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#444444' }}>
                    {stats.paidAmount.toLocaleString('tr-TR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    TL
                  </Typography>
                  <Typography sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                    Ödenen Tutar
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filtreler */}
      <Paper variant="outlined" sx={{ border: 'none', boxShadow: 'none', mb: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <Box sx={{ minWidth: 180 }}>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Durum
              </Typography>
              <TextField
                select
                fullWidth
                size="small"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              >
                <MenuItem value="">Tümü</MenuItem>
                {Object.entries(EventPaymentStatusNames).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ minWidth: 250 }}>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Ara (Ödeyen/Referans/Açıklama)
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearchSubmit();
                  }
                }}
                placeholder="Ödeyen adı, referans numarası veya açıklama ile ara..."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    py: 1.2,
                    px: 1.5,
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Box>

            <Box sx={{ alignSelf: 'flex-end' }}>
              <Button
                variant="contained"
                size="small"
                onClick={handleSearchSubmit}
                sx={{
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  borderRadius: '6px',
                  backgroundColor: '#0E4D94',
                  boxShadow: 'none',
                  py: 1.2,
                  px: 2,
                  '&:hover': {
                    backgroundColor: '#0d3d7a',
                    boxShadow: 'none',
                  },
                }}
              >
                Uygula
              </Button>
            </Box>
          </Stack>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Payment Listesi */}
      {payments.length === 0 ? (
        <Alert severity="info">Filtrelere uygun ödeme kaydı bulunamadı.</Alert>
      ) : (
        <>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)',
              border: '1px solid #f5f5f5',
              py: 1,
              px: 2.5,
            }}
          >
            <Table size="small">
              <TableHead sx={{ bgcolor: 'transparent' }}>
                <TableRow>
                  <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Ödeyen</TableCell>
                  <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Tip</TableCell>
                  <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Katılımcı</TableCell>
                  <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Misafir</TableCell>
                  <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Toplam Tutar</TableCell>
                  <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Ödenen</TableCell>
                  <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Kalan</TableCell>
                  <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Durum</TableCell>
                  <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Oluşturulma</TableCell>
                  <TableCell sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>Ödeme Tarihi</TableCell>
                  <TableCell align="right" sx={{ fontSize: '14px', fontWeight: 500, borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5, color: '#a3a3a3' }}>İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((payment) => (
                  <TableRow key={payment.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleRowClick(payment)}>
                    <TableCell sx={{ fontSize: '14px', color: '#444444', px: 1.5, py: 1.5 }}>{payment.payerName || '-'}</TableCell>
                    <TableCell sx={{ px: 1.5, py: 1.5 }}>
                      <Chip
                        label={EventPayerTypeNames[payment.payerType] || '-'}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '11px',
                          fontWeight: 500,
                          borderRadius: '12px',
                          border: 'none',
                          backgroundColor: '#E7EDF4',
                          color: '#0E4D94',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '14px', color: '#444444', px: 1.5, py: 1.5 }}>{payment.participantCount || 1}</TableCell>
                    <TableCell sx={{ px: 1.5, py: 1.5 }}>
                      {payment.guests && payment.guests.length > 0 ? (
                        <Tooltip
                          title={
                            <Box>
                              <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5, color: '#444444' }}>
                                Misafirler:
                              </Typography>
                              {payment.guests.map((guest, idx) => (
                                <Typography key={idx} sx={{ fontSize: '13px', color: '#444444' }}>
                                  • {guest.fullName}
                                  {guest.phoneNumber && ` (${guest.phoneNumber})`}
                                  {guest.isPaid ? (
                                    <Chip
                                      label="Ödendi"
                                      size="small"
                                      sx={{
                                        ml: 0.5,
                                        height: 18,
                                        fontSize: '10px',
                                        fontWeight: 500,
                                        borderRadius: '10px',
                                        backgroundColor: '#DFFFEA',
                                        color: '#17C653',
                                        border: 'none',
                                      }}
                                    />
                                  ) : (
                                    <Chip
                                      label="Ödenmedi"
                                      size="small"
                                      sx={{
                                        ml: 0.5,
                                        height: 18,
                                        fontSize: '10px',
                                        fontWeight: 500,
                                        borderRadius: '10px',
                                        backgroundColor: '#F5F5F5',
                                        color: '#666666',
                                        border: 'none',
                                      }}
                                    />
                                  )}
                                </Typography>
                              ))}
                            </Box>
                          }
                        >
                          <Chip
                            label={`${payment.guests.length} misafir`}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: '11px',
                              fontWeight: 500,
                              borderRadius: '12px',
                              border: 'none',
                              backgroundColor: payment.guests.every((g) => g.isPaid) ? '#DFFFEA' : '#F5F5F5',
                              color: payment.guests.every((g) => g.isPaid) ? '#17C653' : '#666666',
                            }}
                          />
                        </Tooltip>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '14px', color: '#444444', fontWeight: 500, px: 1.5, py: 1.5 }}>
                      {payment.amount?.toLocaleString('tr-TR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      {getCurrencyDisplayLabel(payment.currency)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '14px', color: '#444444', px: 1.5, py: 1.5 }}>
                      {(payment.paidAmount || 0).toLocaleString('tr-TR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      {getCurrencyDisplayLabel(payment.currency)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '14px', color: '#444444', px: 1.5, py: 1.5 }}>
                      {(payment.remainingAmount || payment.amount || 0).toLocaleString('tr-TR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      {getCurrencyDisplayLabel(payment.currency)}
                    </TableCell>
                    <TableCell sx={{ px: 1.5, py: 1.5 }}>
                      <Chip
                        label={payment.statusName || EventPaymentStatusNames[payment.status]}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '11px',
                          fontWeight: 500,
                          borderRadius: '12px',
                          border: 'none',
                          backgroundColor:
                            payment.status === EventPaymentStatus.Paid
                              ? '#DFFFEA'
                              : payment.status === EventPaymentStatus.AwaitingApproval || payment.status === EventPaymentStatus.PartialPaid
                              ? '#FFF4E6'
                              : payment.status === EventPaymentStatus.Cancelled
                              ? '#F5F5F5'
                              : '#E7EDF4',
                          color:
                            payment.status === EventPaymentStatus.Paid
                              ? '#17C653'
                              : payment.status === EventPaymentStatus.AwaitingApproval || payment.status === EventPaymentStatus.PartialPaid
                              ? '#FF9800'
                              : payment.status === EventPaymentStatus.Cancelled
                              ? '#666666'
                              : '#0E4D94',
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '14px', color: '#444444', px: 1.5, py: 1.5 }}>{formatDateTime(payment.createdAt)}</TableCell>
                    <TableCell sx={{ fontSize: '14px', color: '#444444', px: 1.5, py: 1.5 }}>{formatDateTime(payment.paymentDate)}</TableCell>
                    <TableCell align="right" sx={{ px: 1.5, py: 1.5 }}>
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {payment.status === EventPaymentStatus.AwaitingApproval && (
                          <Tooltip title="Onayla">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPayment(payment);
                                setApproveDialogOpen(true);
                              }}
                              sx={{
                                backgroundColor: '#DFFFEA',
                                borderRadius: '6px',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: '#C8F5D8',
                                },
                                '& svg': {
                                  fontSize: '16px',
                                  color: '#17C653',
                                },
                              }}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {payment.status === EventPaymentStatus.Pending && (
                          <Tooltip title="Ödendi Olarak İşaretle">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPayment(payment);
                                setPaymentMethod(PaymentMethod.Cash);
                                setMarkPaidDialogOpen(true);
                              }}
                              sx={{
                                backgroundColor: '#E7EDF4',
                                borderRadius: '6px',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: '#CFDBEA',
                                },
                                '& svg': {
                                  fontSize: '16px',
                                  color: '#0E4D94',
                                },
                              }}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {payment.status !== EventPaymentStatus.Paid && payment.status !== EventPaymentStatus.Cancelled && (
                          <Tooltip title="İptal Et">
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedPayment(payment);
                                setCancelReason('');
                                setCancelDialogOpen(true);
                              }}
                              sx={{
                                backgroundColor: '#FFF4E6',
                                borderRadius: '6px',
                                width: 32,
                                height: 32,
                                '&:hover': {
                                  backgroundColor: '#FFE8CC',
                                },
                                '& svg': {
                                  fontSize: '16px',
                                  color: '#FF9800',
                                },
                              }}
                            >
                              <CancelIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
              <Typography sx={{ fontSize: '13px', color: '#a3a3a3' }}>
                {totalCount} kayıttan {Math.min((page - 1) * pageSize + 1, totalCount)} - {Math.min(page * pageSize, totalCount)} arası gösteriliyor
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="small"
              />
            </Box>
          )}
        </>
      )}

      {/* Detay Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            color: '#444444',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
            pb: 2,
          }}
        >
          Ödeme Detayı
          <IconButton
            onClick={() => setDetailDialogOpen(false)}
            size="small"
            sx={{
              color: '#a3a3a3',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          {selectedPayment && (
            <Stack spacing={2}>
              <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                <strong>Etkinlik:</strong> {selectedPayment.eventTitle}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                <strong>Ödeyen:</strong> {selectedPayment.payerName}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                <strong>Tip:</strong> {EventPayerTypeNames[selectedPayment.payerType] || '-'}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                <strong>Katılımcı Sayısı:</strong> {selectedPayment.participantCount}
              </Typography>
              
              {/* Host Payment Bilgisi */}
              {selectedPayment.hostPayment && (
                <Box sx={{ mb: 2 }}>
                  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#444444', mb: 1 }}>
                    Host Ödeme Bilgisi:
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1.5, border: '1px solid #e6e6e6', borderRadius: '8px' }}>
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                      <Box>
                        <Typography sx={{ fontSize: '14px', color: '#444444', mb: 0.5 }}>
                          <strong>Durum:</strong>{' '}
                          <Chip
                            label={selectedPayment.hostPayment.statusName || EventPaymentStatusNames[selectedPayment.hostPayment.status]}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: '11px',
                              fontWeight: 500,
                              borderRadius: '12px',
                              border: 'none',
                              backgroundColor: selectedPayment.hostPayment.isPaid ? '#DFFFEA' : '#F5F5F5',
                              color: selectedPayment.hostPayment.isPaid ? '#17C653' : '#666666',
                            }}
                          />
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: '#444444', mb: 0.5 }}>
                          <strong>Tutar:</strong>{' '}
                          {selectedPayment.hostPayment.amount?.toLocaleString('tr-TR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{' '}
                          {getCurrencyDisplayLabel(selectedPayment.hostPayment.currency)}
                        </Typography>
                        {selectedPayment.hostPayment.paymentDate && (
                          <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                            <strong>Ödeme Tarihi:</strong> {formatDateTime(selectedPayment.hostPayment.paymentDate)}
                          </Typography>
                        )}
                      </Box>
                      {!selectedPayment.hostPayment.isPaid && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            setDetailDialogOpen(false);
                            handleOpenPayDialog(selectedPayment, null);
                          }}
                          sx={{
                            textTransform: 'none',
                            fontSize: '13px',
                            fontWeight: 500,
                            borderRadius: '5px',
                            backgroundColor: '#0E4D94',
                            boxShadow: 'none',
                            py: 0.75,
                            px: 1.5,
                            '&:hover': {
                              backgroundColor: '#0d3d7a',
                              boxShadow: 'none',
                            },
                          }}
                        >
                          Host Ödemesi Yap
                        </Button>
                      )}
                    </Stack>
                  </Paper>
                </Box>
              )}

              {/* Guest Listesi */}
              {selectedPayment.guests && selectedPayment.guests.length > 0 && (
                <Box>
                  <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#444444', mb: 1 }}>
                    Misafirler ({selectedPayment.guests.length}):
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ border: '1px solid #e6e6e6', borderRadius: '8px' }}>
                    <Table size="small">
                      <TableHead sx={{ bgcolor: 'transparent' }}>
                        <TableRow>
                          <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#a3a3a3', borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5 }}>Ad Soyad</TableCell>
                          <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#a3a3a3', borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5 }}>Telefon</TableCell>
                          <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#a3a3a3', borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5 }}>Tutar</TableCell>
                          <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#a3a3a3', borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5 }}>Durum</TableCell>
                          <TableCell sx={{ fontSize: '13px', fontWeight: 500, color: '#a3a3a3', borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5 }}>Ödeme Tarihi</TableCell>
                          <TableCell align="right" sx={{ fontSize: '13px', fontWeight: 500, color: '#a3a3a3', borderBottom: '1px dashed #e8e8e8', px: 1.5, py: 1.5 }}>İşlem</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedPayment.guests.map((guest, idx) => (
                          <TableRow key={idx}>
                            <TableCell sx={{ fontSize: '14px', color: '#444444', px: 1.5, py: 1.5 }}>{guest.fullName}</TableCell>
                            <TableCell sx={{ fontSize: '14px', color: '#444444', px: 1.5, py: 1.5 }}>{guest.phoneNumber || '-'}</TableCell>
                            <TableCell sx={{ fontSize: '14px', color: '#444444', fontWeight: 500, px: 1.5, py: 1.5 }}>
                              {guest.amount?.toLocaleString('tr-TR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{' '}
                              {getCurrencyDisplayLabel(guest.currency)}
                            </TableCell>
                            <TableCell sx={{ px: 1.5, py: 1.5 }}>
                              <Chip
                                label={guest.statusName || (guest.isPaid ? 'Ödendi' : 'Ödenmedi')}
                                size="small"
                                sx={{
                                  height: 24,
                                  fontSize: '11px',
                                  fontWeight: 500,
                                  borderRadius: '12px',
                                  border: 'none',
                                  backgroundColor: guest.isPaid ? '#DFFFEA' : '#F5F5F5',
                                  color: guest.isPaid ? '#17C653' : '#666666',
                                }}
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: '14px', color: '#444444', px: 1.5, py: 1.5 }}>{formatDateTime(guest.paymentDate)}</TableCell>
                            <TableCell align="right" sx={{ px: 1.5, py: 1.5 }}>
                              {!guest.isPaid && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    setDetailDialogOpen(false);
                                    handleOpenPayDialog(selectedPayment, guest.guestId);
                                  }}
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    borderRadius: '5px',
                                    borderColor: '#0E4D94',
                                    color: '#0E4D94',
                                    '&:hover': {
                                      borderColor: '#0a3d7a',
                                      backgroundColor: 'rgba(14, 77, 148, 0.04)',
                                    },
                                  }}
                                >
                                  Ödeme Yap
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              
              <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                <strong>Toplam Tutar:</strong>{' '}
                {selectedPayment.amount?.toLocaleString('tr-TR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                {getCurrencyDisplayLabel(selectedPayment.currency)}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                <strong>Ödenen Tutar:</strong>{' '}
                {(selectedPayment.paidAmount || 0).toLocaleString('tr-TR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                {getCurrencyDisplayLabel(selectedPayment.currency)}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                <strong>Kalan Tutar:</strong>{' '}
                {(selectedPayment.remainingAmount || selectedPayment.amount || 0).toLocaleString('tr-TR', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                {getCurrencyDisplayLabel(selectedPayment.currency)}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                <strong>Durum:</strong> {selectedPayment.statusName || EventPaymentStatusNames[selectedPayment.status]}
              </Typography>
              {selectedPayment.paymentMethod !== null && selectedPayment.paymentMethod !== undefined && (
                <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                  <strong>Ödeme Yöntemi:</strong> {selectedPayment.paymentMethodName || PaymentMethodNames[selectedPayment.paymentMethod]}
                </Typography>
              )}
              {selectedPayment.referenceNumber && (
                <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                  <strong>Referans Numarası:</strong> {selectedPayment.referenceNumber}
                </Typography>
              )}
              {selectedPayment.description && (
                <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                  <strong>Açıklama:</strong> {selectedPayment.description}
                </Typography>
              )}
              {selectedPayment.paymentProofUrl && (
                <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                  <strong>Ödeme Kanıtı:</strong>{' '}
                  <a href={selectedPayment.paymentProofUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#0E4D94', textDecoration: 'none' }}>
                    Görüntüle
                  </a>
                </Typography>
              )}
              <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                <strong>Oluşturulma:</strong> {formatDateTime(selectedPayment.createdAt)}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                <strong>Ödeme Tarihi:</strong> {formatDateTime(selectedPayment.paymentDate)}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setDetailDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              color: '#444444',
            }}
          >
            Kapat
          </Button>
          {canApprove && (
            <Button
              onClick={() => {
                setDetailDialogOpen(false);
                setApproveDialogOpen(true);
              }}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '6px',
                backgroundColor: '#17C653',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#15b04a',
                  boxShadow: 'none',
                },
              }}
            >
              Onayla
            </Button>
          )}
          {canMarkPaid && (
            <Button
              onClick={() => {
                setDetailDialogOpen(false);
                setMarkPaidDialogOpen(true);
              }}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '6px',
                backgroundColor: '#0E4D94',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#0d3d7a',
                  boxShadow: 'none',
                },
              }}
            >
              Ödendi Olarak İşaretle
            </Button>
          )}
          {canCancel && (
            <Button
              onClick={() => {
                setDetailDialogOpen(false);
                setCancelDialogOpen(true);
              }}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 500,
                borderRadius: '6px',
                backgroundColor: '#FF9800',
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: '#e69400',
                  boxShadow: 'none',
                },
              }}
            >
              İptal Et
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Onay Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            color: '#444444',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
            pb: 2,
          }}
        >
          Ödemeyi Onayla
          <IconButton
            onClick={() => setApproveDialogOpen(false)}
            size="small"
            sx={{
              color: '#a3a3a3',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Typography sx={{ fontSize: '14px', color: '#444444', mt: 1 }}>
            Bu ödemeyi <strong>onaylamak</strong> istediğinize emin misiniz?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setApproveDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              color: '#444444',
            }}
          >
            İptal
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              borderRadius: '6px',
              backgroundColor: '#17C653',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#15b04a',
                boxShadow: 'none',
              },
            }}
          >
            Onayla
          </Button>
        </DialogActions>
      </Dialog>

      {/* İptal Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            color: '#444444',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
            pb: 2,
          }}
        >
          Ödemeyi İptal Et
          <IconButton
            onClick={() => setCancelDialogOpen(false)}
            size="small"
            sx={{
              color: '#a3a3a3',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Typography sx={{ fontSize: '14px', color: '#444444', mt: 1, mb: 2 }}>
            Bu ödemeyi <strong>iptal etmek</strong> istediğinize emin misiniz?
          </Typography>
          <TextField
            fullWidth
            label="İptal nedeni (opsiyonel)"
            multiline
            minRows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '5px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0E4D94',
                },
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e6e6e6',
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              color: '#444444',
            }}
          >
            Vazgeç
          </Button>
          <Button
            onClick={handleCancel}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              borderRadius: '6px',
              backgroundColor: '#FF9800',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#e69400',
                boxShadow: 'none',
              },
            }}
          >
            İptal Et
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ödendi Olarak İşaretle Dialog */}
      <Dialog open={markPaidDialogOpen} onClose={() => setMarkPaidDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            color: '#444444',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
            pb: 2,
          }}
        >
          Ödendi Olarak İşaretle
          <IconButton
            onClick={() => setMarkPaidDialogOpen(false)}
            size="small"
            sx={{
              color: '#a3a3a3',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Typography sx={{ fontSize: '14px', color: '#444444', mt: 1, mb: 2 }}>
            Bu ödemeyi <strong>ödendi</strong> olarak işaretlemek istediğinize emin misiniz?
          </Typography>
          <TextField
            fullWidth
            select
            label="Ödeme Yöntemi"
            size="small"
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(Number(e.target.value))}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '5px',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#0E4D94',
                },
              },
              '& .MuiInputBase-input': {
                fontSize: '14px',
              },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#e6e6e6',
              },
            }}
          >
            {Object.entries(PaymentMethodNames).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setMarkPaidDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              color: '#444444',
            }}
          >
            Vazgeç
          </Button>
          <Button
            onClick={handleMarkPaid}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              borderRadius: '6px',
              backgroundColor: '#0E4D94',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#0d3d7a',
                boxShadow: 'none',
              },
            }}
          >
            İşaretle
          </Button>
        </DialogActions>
      </Dialog>

      {/* Guest/Host Ödeme Dialog */}
      <Dialog open={payGuestHostDialogOpen} onClose={() => setPayGuestHostDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            color: '#444444',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
            pb: 2,
          }}
        >
          {selectedGuestId ? 'Misafir Ödemesi Yap' : 'Host Ödemesi Yap'}
          <IconButton
            onClick={() => setPayGuestHostDialogOpen(false)}
            size="small"
            sx={{
              color: '#a3a3a3',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 3, py: 2 }}>
          {selectedPayment && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                <strong>Ödeyen:</strong> {selectedPayment.payerName}
              </Typography>
              {selectedGuestId ? (
                <>
                  {selectedPayment.guests?.find((g) => g.guestId === selectedGuestId) && (
                    <>
                      <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                        <strong>Misafir:</strong> {selectedPayment.guests.find((g) => g.guestId === selectedGuestId).fullName}
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                        <strong>Tutar:</strong>{' '}
                        {selectedPayment.guests.find((g) => g.guestId === selectedGuestId).amount?.toLocaleString('tr-TR', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        {getCurrencyDisplayLabel(selectedPayment.guests.find((g) => g.guestId === selectedGuestId).currency)}
                      </Typography>
                    </>
                  )}
                </>
              ) : (
                <>
                  {selectedPayment.hostPayment && (
                    <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                      <strong>Tutar:</strong>{' '}
                      {selectedPayment.hostPayment.amount?.toLocaleString('tr-TR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      {getCurrencyDisplayLabel(selectedPayment.hostPayment.currency)}
                    </Typography>
                  )}
                </>
              )}
              <TextField
                fullWidth
                select
                label="Ödeme Yöntemi"
                size="small"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(Number(e.target.value))}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              >
                {Object.entries(PaymentMethodNames).map(([key, label]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                fullWidth
                label="Referans Numarası (Opsiyonel)"
                size="small"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder="Banka havalesi referans numarası"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
              <TextField
                fullWidth
                label="Açıklama (Opsiyonel)"
                size="small"
                multiline
                minRows={2}
                value={paymentDescription}
                onChange={(e) => setPaymentDescription(e.target.value)}
                placeholder="Ödeme ile ilgili notlar"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '5px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#0E4D94',
                    },
                  },
                  '& .MuiInputBase-input': {
                    fontSize: '14px',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#e6e6e6',
                  },
                }}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setPayGuestHostDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              color: '#444444',
            }}
          >
            Vazgeç
          </Button>
          <Button
            onClick={handlePayForGuestOrHost}
            variant="contained"
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 500,
              borderRadius: '6px',
              backgroundColor: '#0E4D94',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#0d3d7a',
                boxShadow: 'none',
              },
            }}
          >
            Ödeme Yap
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventPaymentDetail;
